import { useLayoutEffect, useState } from "react";
import { Button, Col, Container, Dropdown, Row } from "react-bootstrap";
import { CommonToolTip, CstmPagination } from "components/Common/Common";
import { Link, useNavigate } from "react-router-dom";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { MANAGE_PARENTS_EDIT, MANAGE_PARENTS_ADD } from "config/paths";
import { convert12HourTime, convertToMomentDate, dateTimeFormat, successMessageToast } from "helpers/common";
import LoadingOverlay from "components/shared/LoadingOverlay";
import { ChatIcon, DeleteIcon, EditIcon, ResendMailIcon, RestoreIcon } from "Assets/icons";
import TableLayout from "components/TableLayout";
import { useDeleteParentMutation } from "store/services/parents";
import ErrorComponent from "components/shared/ErrorComponent";
import TableHeader from "components/shared/FormComponents/TableHeader";
import { useDispatch, useSelector } from "react-redux";
import { initialState, parentsFilter } from "store/slices/filter";
import { useGetUsersWithRoleQuery, useResendAdminAccountActivationMailMutation, useUpdateSubAdminAccountStatusMutation, useUpdateSubAdminLoginStatusMutation } from "store/services/users/role";
import { STATUS_OPTIONS, SUB_ADMIN_LOGIN_STATUS_OPTIONS, SUB_ADMIN_STATUS_OPTIONS } from "config/constants";
import AlertPop from "components/Modals/AlertPop";
import confirmDialog from "utils/confirmationDialog";
import MailSentPopup from "./MailSentPopup";

const ManageParents = () => {

    const navigate = useNavigate();

    const dispatch = useDispatch();
    const filter = useSelector(state => state?.filter?.parentsFilter);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [parentsList, setParentsList] = useState([]);
    const [showAlertModal, setShowAlertModal] = useState(false);

    const [deleteParent, { isLoading: deleteParentLoading }] = useDeleteParentMutation();

    // Activation mail
    const [resendAccountActivation, { isLoading: resendMailLoading }] = useResendAdminAccountActivationMailMutation();

    // update account status
    const [updateAccountStatus, { isLoading: updateAccountStatusLoading }] = useUpdateSubAdminAccountStatusMutation();

    // update login status
    const [updateLoginStatus, { isLoading: updateLoginStatusLoading }] = useUpdateSubAdminLoginStatusMutation();

    const { data, isLoading, isError, isFetching, refetch } = useGetUsersWithRoleQuery({ page: currentPage, limit: pageSize, ...filter });
    const totalRecords = data?.totalRecords || 0;
    const totalPages = Math.ceil(totalRecords / pageSize);


    const handleDelete = async (data) => {
        try {
            let isConfirm = await confirmDialog();
            if (!isConfirm) return;

            const list = [...parentsList];
            const index = parentsList?.findIndex(x => x.id === data?.id);
            delete list[index];
            setParentsList(list.filter(Boolean));

            const result = await deleteParent(data?.id);
            if (!result?.error) successMessageToast(result?.data?.message);

        } catch (err) { console.error("error deleting parent", err); };
    };

    const handleResendMail = async (data, bypass = false) => {
        console.log('data', data)
        try {

            if (!bypass) {
                let isConfirm = await confirmDialog({ title: `Are you sure you want to resend the email invitation to ${data?.firstname} ${data?.lastname} ?` });
                if (!isConfirm) return;
            }

            let result = await resendAccountActivation(data?.id);
            if (!result.error) {
                if (bypass) {
                    setSendMailPopup(true)
                }
                else {
                    successMessageToast("Account activation mail has been sent successfully.");
                }
            }
        } catch (err) {
            console.log("error resend activation mail", err);
        }
    };

    const handleStatusUpdate = async (data) => {
        const statusName = data?.type == "status" ? SUB_ADMIN_LOGIN_STATUS_OPTIONS?.find((item) => item?.value == data?.status)?.label : SUB_ADMIN_STATUS_OPTIONS?.find((item) => item?.value == data?.status)?.label
        console.log('data', data)
        try {
            let isConfirm = data?.type == "status" ? await confirmDialog({ title: `Are you sure you want to Mark ${data?.user?.firstname} ${data?.user?.lastname}'s status to ${statusName} ?` }) : await confirmDialog({ title: `Are you sure you want mark ${data?.user?.firstname} ${data?.user?.lastname}'s login status to ${statusName}?` });
            if (!isConfirm) return;

            const result = data?.type == "status" ? await updateAccountStatus(data) : data?.type == "loginStatus" ? await updateLoginStatus(data) : { error: "" };
            if (result?.error) return;

            if (data?.status == "active" && data?.type == "loginStatus" && !data?.user?.email_verified_at) {
                handleResendMail(data?.user, true)
            }

            successMessageToast(result?.data?.message);
        } catch (err) { console.log("error update status", err); }
    };

    const column = [
        {
            head: "Last Name",
            accessor: "lastname",
            isComponent: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{item?.lastname || "--"}</h6>
            ),
        },
        {
            head: "First Name",
            accessor: "firstname",
            isComponent: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{item?.firstname || "--"}</h6>
            ),
        },
        {
            head: "Phone Number",
            accessor: "phone",
            component: (item, ind) => (
                <p>{item?.phone || "--"}</p>
            ),
        },
        {
            head: "Email",
            accessor: "email",
            isComponent: true,
            component: (item, ind) => (
                <p className="cell-text-ellipsis">{item?.email || "--"}</p>
            ),
        },
        {
            head: "Activation Date",
            accessor: "selectAll",
            isComponent: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{convertToMomentDate(item?.createdAt)}</h6>
            ),
        },
        {
            head: "Last Login",
            isComponent: true,
            component: (item, ind) => (
                <h6 className="m-0 fw-bold themeBlue">{dateTimeFormat(item?.last_login_at)}</h6>
            ),
        },
        {
            head: "Verification Mail Sent",
            accessor: "selectAll",
            isComponent: true,
            component: (item, ind) => (
                <p
                    style={{ width: 80 }}
                    className={`${item?.email_activation_sent == "yes"
                        ? "greenStatus"
                        : "redStatus"
                        } status fw-bold text-capitalize m-0 text-center d-flex align-items-center justify-content-center rounded-pill px-4 py-1`}
                >
                    {item?.email_activation_sent}
                </p>
            ),
        },
        {
            head: "Status",
            accessor: "status",
            isComponent: true,
            component: (item) => (
                <div onClick={e => e.stopPropagation()}>
                    <Dropdown
                        onSelect={(value) => handleStatusUpdate({ type: "status", userId: item?.id, status: value, user: item })}
                    >
                        <Dropdown.Toggle
                            disabled={isFetching || updateAccountStatusLoading}
                            variant="transparent"
                            className="border d-flex align-items-center  justify-content-between"
                            id="dropdown-basic"
                            style={{ minWidth: "100px" }}
                        >

                            {SUB_ADMIN_STATUS_OPTIONS?.find(x => x.value == item?.status)?.label || "Status"}                                                        <span className="icn ms-2">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="8"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                >
                                    <path
                                        d="M1.415 0.298828L6 4.731L10.585 0.298828L12 1.66666L6 7.46666L0 1.66666L1.415 0.298828Z"
                                        fill="black"
                                    />
                                </svg>
                            </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu >
                            {
                                SUB_ADMIN_STATUS_OPTIONS?.map((item, index) => {
                                    if (item.value) return <Dropdown.Item key={index} eventKey={item?.value}>{item?.label}</Dropdown.Item>
                                })
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            ),
        },
        {
            head: "Login Status",
            accessor: "isLoginAllowed",
            isComponent: true,
            component: (item) => (
                <div onClick={e => e.stopPropagation()}>
                    <Dropdown
                        onSelect={(value) => handleStatusUpdate({ type: "loginStatus", userId: item?.id, status: value, user: item })}
                    >
                        <Dropdown.Toggle
                            disabled={isFetching || updateLoginStatusLoading}
                            variant="transparent"
                            className="border d-flex align-items-center  justify-content-between"
                            id="dropdown-basic"
                            style={{ minWidth: "100px" }}
                        >

                            {SUB_ADMIN_LOGIN_STATUS_OPTIONS?.find(x => x.value == item?.isLoginAllowed)?.label || "Login Status"}                                                        <span className="icn ms-2">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="8"
                                    viewBox="0 0 12 8"
                                    fill="none"
                                >
                                    <path
                                        d="M1.415 0.298828L6 4.731L10.585 0.298828L12 1.66666L6 7.46666L0 1.66666L1.415 0.298828Z"
                                        fill="black"
                                    />
                                </svg>
                            </span>
                        </Dropdown.Toggle>

                        <Dropdown.Menu >
                            {
                                SUB_ADMIN_LOGIN_STATUS_OPTIONS?.map((item, index) => {
                                    return <Dropdown.Item key={index} eventKey={item?.value}>{item?.label}</Dropdown.Item>
                                })
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            ),
        },
        {
            head: "Action",
            lastIndexSticky: true,
            accessor: "",
            isComponent: true,
            component: (item) => (
                <div className="ActnBtn d-flex align-items-center  gap-10">
                    <CommonToolTip
                        text={"Edit"}
                        component={<Button
                            className="p-0"
                            variant="transparent"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(MANAGE_PARENTS_EDIT.replace(":id", item.id)) }}
                        >
                            <EditIcon />
                        </Button>}
                    />
                    {/* <CommonToolTip
                        text={"Resend Mail"}
                        component={<Button
                            className="p-0"
                            // variant="transparent"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                if (item?.email_verified_at) {
                                    setShowAlertModal(true);
                                    return;
                                };
                                handleResendMail(item);
                            }}
                        >
                            Resend Mail
                        </Button>}
                    /> */}
                    <CommonToolTip
                        text={"Delete"}
                        component={<Button
                            className="p-0"
                            variant="transparent"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item);
                            }}
                        >
                            <DeleteIcon />
                        </Button>}
                    />
                    <Button
                        type="button"
                        className="btn text-white d-flex align-items-center justify-content-center commonBtn fw-sbold"
                        style={{ height: "30px", minWidth: "60px" }}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (item?.email_verified_at) {
                                setShowAlertModal(true);
                                return;
                            };
                            handleResendMail(item);
                        }}

                    >
                        Resend Mail
                    </Button>
                </div>
            ),
        },
    ]

    const handleFilterChange = (key) => (value) => {
        dispatch(parentsFilter({ ...filter, [key]: value }))
        setCurrentPage(1);
    }

    const handlePageChange = (page) => {
        window.scrollTo(0, 0);
        setCurrentPage(page);
    };

    const resetFilter = () => {
        dispatch(parentsFilter(initialState.parentsFilter));
        setCurrentPage(1)
    };

    useLayoutEffect(() => {

        if (data?.records) setParentsList(data?.records)
    }, [data]);
    const [sendMailPopup, setSendMailPopup] = useState(false)
    return (
        <>
            <MailSentPopup confirmation={sendMailPopup} setConfirmation={setSendMailPopup} />
            <AlertPop
                confirmation={showAlertModal}
                setConfirmation={setShowAlertModal}
                message={"The account has been already verified"}
            />
            <section className="ManageDiscriplines position-relative py-4">
                <Container fluid={true}>
                    <Row>
                        <Col lg="12" className="my-2">
                            <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                                <div className="left">
                                    <TableHeader
                                        statusOptions={SUB_ADMIN_STATUS_OPTIONS}
                                        heading={"Parent Profiles"}
                                        handleFilterChange={handleFilterChange}
                                        resetFilter={resetFilter}
                                        filter={filter}
                                        searchPlaceholder="Email, First, Last Name"
                                    />
                                </div>
                                <div className="right">
                                    <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center justify-content-center flex-wrap gap-10">
                                        <li className="">
                                            <Link
                                                to={MANAGE_PARENTS_ADD}
                                                className="btn text-white d-flex align-items-center justify-content-center commonBtn fw-sbold "
                                            >
                                                <span className="icn me-1">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="19"
                                                        height="19"
                                                        viewBox="0 0 19 19"
                                                        fill="none"
                                                    >
                                                        <mask
                                                            id="mask0_0_4440"
                                                            style={{ maskType: "alpha" }}
                                                            maskUnits="userSpaceOnUse"
                                                            x="0"
                                                            y="0"
                                                            width="19"
                                                            height="19"
                                                        >
                                                            <rect
                                                                width="18.7197"
                                                                height="18.7197"
                                                                fill="#D9D9D9"
                                                            />
                                                        </mask>
                                                        <g mask="url(#mask0_0_4440)">
                                                            <path
                                                                d="M8.57996 13.2594H10.1399V10.1394H13.2599V8.57947H10.1399V5.45951H8.57996V8.57947H5.46V10.1394H8.57996V13.2594ZM9.35995 17.1593C8.28096 17.1593 7.26698 16.9546 6.31799 16.5451C5.369 16.1356 4.54352 15.5799 3.84153 14.8779C3.13954 14.1759 2.58379 13.3504 2.1743 12.4014C1.76481 11.4524 1.56006 10.4384 1.56006 9.35946C1.56006 8.28047 1.76481 7.26649 2.1743 6.3175C2.58379 5.36851 3.13954 4.54303 3.84153 3.84104C4.54352 3.13905 5.369 2.58331 6.31799 2.17381C7.26698 1.76432 8.28096 1.55957 9.35995 1.55957C10.4389 1.55957 11.4529 1.76432 12.4019 2.17381C13.3509 2.58331 14.1764 3.13905 14.8784 3.84104C15.5804 4.54303 16.1361 5.36851 16.5456 6.3175C16.9551 7.26649 17.1598 8.28047 17.1598 9.35946C17.1598 10.4384 16.9551 11.4524 16.5456 12.4014C16.1361 13.3504 15.5804 14.1759 14.8784 14.8779C14.1764 15.5799 13.3509 16.1356 12.4019 16.5451C11.4529 16.9546 10.4389 17.1593 9.35995 17.1593ZM9.35995 15.5994C11.1019 15.5994 12.5774 14.9949 13.7864 13.7859C14.9954 12.5769 15.5999 11.1014 15.5999 9.35946C15.5999 7.61748 14.9954 6.142 13.7864 4.93302C12.5774 3.72404 11.1019 3.11955 9.35995 3.11955C7.61797 3.11955 6.14249 3.72404 4.93351 4.93302C3.72453 6.142 3.12004 7.61748 3.12004 9.35946C3.12004 11.1014 3.72453 12.5769 4.93351 13.7859C6.14249 14.9949 7.61797 15.5994 9.35995 15.5994Z"
                                                                fill="white"
                                                            />
                                                        </g>
                                                    </svg>
                                                </span>{" "}
                                                Add New Parent
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="table-outer">
                                {isLoading ? (
                                    <div style={{ height: "200px" }}>
                                        <LoadingIndicator />
                                    </div>
                                ) : isError ? (
                                    <ErrorComponent />
                                ) : (
                                    <>
                                        {(isFetching || deleteParentLoading || resendMailLoading || updateLoginStatusLoading || updateAccountStatusLoading) && (
                                            <LoadingOverlay />
                                        )}
                                        <TableLayout
                                            url={"/manage-parents/details"}
                                            data={parentsList}
                                            column={column}
                                            filter={filter}
                                            handleSort={handleFilterChange("sort")}
                                        // lastIndexSticky={true}
                                        />
                                    </>
                                )}
                                <CstmPagination
                                    totalPages={totalPages}
                                    activePage={currentPage}
                                    handlePageChange={handlePageChange}
                                />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default ManageParents;



